<template>
  <div>

    <div v-if="!show404 && !show403 && !independenceDeclaration.status">

      <!-- Engagements: hero with key facts, review progress and the phases -->
      <EngagementHero v-if="isEngagementProject && engagement.steps"
                      :project="activity"
                      :engagement="engagement"
                      @open-tab="openEngagementTab"
      >
        <template #actions>
          <ProjectEditForm
              project-page
              :object="activity"
              :edit="edit"
              @reloadContent="reloadContent"
              v-if="isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && canManageProject && activity.status !== 'closed' && activity.status !== 'completed'"
          ></ProjectEditForm>
          <!-- Settings and History open from the hero instead of the tab bar -->
          <span class="btn btn-edit"
                :class="{'btn-edit--on': activeEngagementTab === 'settings'}"
                v-if="canManageProject"
                @click="openEngagementTab('settings')"
                v-b-tooltip.hover
                :title="$t('projects.engagement.tab.settings')"
                data-qid="engagement-open-settings"
          >
            <font-awesome-icon icon="cog"/>
          </span>
          <span class="btn btn-edit"
                :class="{'btn-edit--on': activeEngagementTab === 'history'}"
                v-if="(activity.additional_client && getClient.slug !== activity.additional_client.slug) || activity.additional_client === null"
                @click="openEngagementTab('history')"
                v-b-tooltip.hover
                :title="$t('projects.tab.history')"
                data-qid="engagement-open-history"
          >
            <font-awesome-icon icon="history"/>
          </span>
          <span class="btn btn-delete"
                data-qid="delete-s-button"
                v-if="isGranted([permissions.PROJECT_DELETE, permissions.PROJECT_DELETE_OWN], activity) && !activity.hasRunningProjects"
                @click="showConfirmDeleteModal(activity)"
          >
            <font-awesome-icon icon="trash-alt"/>
          </span>
        </template>
      </EngagementHero>

      <!-- Recurring projects: a command centre for their rounds (US 9396) -->
      <RecurringProjectHero v-else-if="isRecurringProject"
                            :project="activity"
                            :canRun="canManageProject && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity)"
                            @run="runRound"
                            @open-tab="openRecurringTab"
      >
        <template #actions>
          <span v-if="(activity.additional_client && getClient.slug !== activity.additional_client.slug) || activity.additional_client == null">
            <ProjectEditForm
                project-page
                :object="activity"
                :edit="edit"
                @reloadContent="reloadContent"
                v-if="isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && canManageProject"
            ></ProjectEditForm>
            <!-- Settings and History open from the hero instead of the tab bar, like on engagements -->
            <span class="btn btn-edit"
                  :class="{'btn-edit--on': activeEngagementTab === 'settings'}"
                  @click="openRecurringTab('base')"
                  v-b-tooltip.hover
                  :title="$t('projects.engagement.tab.settings')"
                  data-qid="recurring-open-settings"
            >
              <font-awesome-icon icon="cog"/>
            </span>
            <span class="btn btn-edit"
                  :class="{'btn-edit--on': activeEngagementTab === 'history'}"
                  @click="openRecurringTab('history')"
                  v-b-tooltip.hover
                  :title="$t('projects.tab.history')"
                  data-qid="recurring-open-history"
            >
              <font-awesome-icon icon="history"/>
            </span>
            <span class="btn btn-delete"
                  data-qid="delete-s-button"
                  v-if="isGranted([permissions.PROJECT_DELETE, permissions.PROJECT_DELETE_OWN], activity) && !activity.hasRunningProjects"
                  @click="showConfirmDeleteModal(activity)"
            >
              <font-awesome-icon icon="trash-alt"/>
            </span>
          </span>
        </template>
      </RecurringProjectHero>

      <!-- The hero's outline while the project and its engagement load, so the tabs don't move down afterwards -->
      <div v-else-if="!activity.id || isEngagementProject" class="project-hero is-loading" aria-hidden="true" data-qid="project-hero-loading">
        <div class="project-hero__top">
          <div class="project-hero__main">
            <span class="sx-skel is-crumb"></span>
            <span class="sx-skel is-eyebrow"></span>
            <span class="sx-skel is-title"></span>
            <span class="sx-skel is-pills"></span>
            <span class="sx-skel is-facts"></span>
            <span class="sx-skel is-button"></span>
          </div>
          <div class="project-hero__progress">
            <span class="sx-skel is-ring"></span>
            <div>
              <span class="sx-skel is-long"></span>
              <span class="sx-skel is-short"></span>
            </div>
            <div class="project-hero__stats">
              <span class="sx-skel is-stat" v-for="n in 3" :key="'hero-stat-' + n"></span>
            </div>
          </div>
        </div>
        <div class="project-hero__stepper">
          <div class="hero-step" v-for="n in 4" :key="'hero-step-' + n">
            <span class="hero-step__dot"></span>
            <div class="hero-step__text">
              <span class="sx-skel is-name"></span>
              <span class="sx-skel is-sub"></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Other one-time projects: the same kind of hero, with Settings and History in it (US 9478) -->
      <ProjectHero v-else :project="activity">
        <template #actions>
          <span v-if="(activity.additional_client && getClient.slug !== activity.additional_client.slug) || activity.additional_client == null">
            <ProjectEditForm
                project-page
                :object="activity"
                :edit="edit"
                @reloadContent="reloadContent"
                v-if="isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && canManageProject && activity.status !== 'closed' && activity.status !== 'completed'"
            ></ProjectEditForm>
            <span class="btn btn-edit"
                  :class="{'btn-edit--on': activeEngagementTab === 'settings'}"
                  @click="openEngagementTab('settings')"
                  v-b-tooltip.hover
                  :title="$t('projects.engagement.tab.settings')"
                  data-qid="project-open-settings"
            >
              <font-awesome-icon icon="cog"/>
            </span>
            <span class="btn btn-edit"
                  :class="{'btn-edit--on': activeEngagementTab === 'history'}"
                  @click="openEngagementTab('history')"
                  v-b-tooltip.hover
                  :title="$t('projects.tab.history')"
                  data-qid="project-open-history"
            >
              <font-awesome-icon icon="history"/>
            </span>
            <span class="btn btn-delete"
                  data-qid="delete-s-button"
                  v-if="isGranted([permissions.PROJECT_DELETE, permissions.PROJECT_DELETE_OWN], activity) && !activity.hasRunningProjects"
                  @click="showConfirmDeleteModal(activity)"
            >
              <font-awesome-icon icon="trash-alt"/>
            </span>
          </span>
        </template>
      </ProjectHero>

      <ProjectLockedNotice :project="activity" class="mt-3 mb-0"></ProjectLockedNotice>

      <b-tabs class="mt-3" v-model="tabIndex" nav-class="engagement-tabnav">

        <!-- Recurring project overview tab -->
        <b-tab :title="$t('projects.management.views.overview')" v-if="isRecurringProject" active ref="tab-recurring-overview" class="mt-3">
          <RecurringProjectOverview :project="activity"
                                    :key="'recurring-' + activity.updated_at + '-' + roundsKey"
                                    @open-tab="openRecurringTab"
          ></RecurringProjectOverview>
        </b-tab>

        <!-- Basic info tab (the Settings of a recurring project, opened from its hero) -->
        <b-tab :title="isRecurringProject ? $t('projects.engagement.tab.settings') : $t('projects.engagement.tab.overview')"
               :active="!isRecurringProject"
               :title-item-class="isRecurringProject ? 'd-none' : ''"
               @click="basicTabClicked(); refreshEngagement()"
               ref="tab-overview"
        >
          <div class="mt-3">
            <EngagementOverview
                v-if="isEngagementProject && engagement.steps"
                :project="activity"
                :engagement="engagement"
                @changed="engagementChanged"
                @media-changed="loadContent"
                @open-tab="openEngagementTab"
            >
              <template #aside>
                <EngagementScheduleCard :project="activity"></EngagementScheduleCard>
                <!-- Budget and time is also here so Associates (no Planning tab) can log time -->
                <EngagementBudget
                    :project="activity"
                    :engagement="engagement"
                    @changed="engagementChanged"
                    @reload="loadEngagement"
                ></EngagementBudget>
                <EngagementTeam :project="activity" @updated="loadContent"></EngagementTeam>
                <EngagementRecentActivity :project="activity" :key="'recent-' + activity.updated_at" @open-tab="openEngagementTab"></EngagementRecentActivity>
              </template>
            </EngagementOverview>

            <!-- Other one-time projects: description, scope and contacts, with schedule, contributors and activity beside them (US 9478) -->
            <div class="v2-grid" v-if="isOneTimeProject" data-qid="project-overview">
              <div class="v2-col">
                <div class="panel" data-qid="project-description">
                  <header class="panel__header">
                    <div class="profile-compact">
                      <div class="logo-container">
                        <font-awesome-icon icon="text" class="icon-svg--sm"/>
                      </div>
                      <div class="profile-compact__info">
                        <div class="profile-compact__top">
                          <div class="">
                            {{ $t('activities.description_label') }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="profile-compact__actions">
                      <span class="icon-btn"
                            @click="showDescriptionInputFunction"
                            data-qid="activity-edit-description"
                            v-if="(!showDescriptionInput && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && canManageProject && activity.status !== 'closed' && activity.status !== 'completed') && ((activity.additional_client && getClient.slug !== activity.additional_client.slug) || activity.additional_client == null)"
                      >
                        <font-awesome-icon icon="pen"/>
                      </span>
                      <span class="icon-btn btn-delete"
                            @click="cancelDescriptionForm"
                            data-qid="activity-edit-button-cancel"
                            v-if="showDescriptionInput"
                      >
                        <font-awesome-icon icon="rotate-left"/>
                      </span>
                      <span class="icon-btn btn-edit"
                            @click="formDescriptionSubmit"
                            data-qid="activity-edit-button-submit"
                            v-if="showDescriptionInput"
                      >
                        <font-awesome-icon icon="save"/>
                      </span>
                    </div>
                  </header>

                  <div class="panel__body">
                    <div class="card" data-qid="basic-info-section">
                      <div class="">
                        <div class="content-view-body">
                          <div>
                            <div v-if="showDescriptionInput">
                              <RichTextEditor
                                qid="edit-tp-form-description"
                                v-model="activityEditDescription"
                                :rows="6"
                                :headings="false"
                                compact
                                :placeholder="$t('maintenance.third_parties.create.description_placeholder')"
                              />
                            </div>

                            <div v-if="!showDescriptionInput" class="section-container">
                              <div v-if="activity.description && activity.description.length > descriptionLength">
                                <RichTextView v-if="showFullDescription" :value="activity.description" :headings="false" compact class="text-formatted"/>
                                <p v-if="!showFullDescription" class="text-formatted"><RichTextView :value="activity.description" :headings="false" compact :truncate="descriptionLength"/></p>
                                <div class="text-center mt-3" @click="showFullDescription=!showFullDescription">
                                  <span class="cursor-pointer" v-if="!showFullDescription">
                                    <span class="mr-2">
                                      {{ $t('system.view_more') }}
                                    </span>
                                    <font-awesome-icon icon="chevron-down"/>
                                  </span>
                                  <span class="cursor-pointer" v-if="showFullDescription">
                                    <span class="mr-2">
                                      {{ $t('system.view_less') }}
                                    </span>
                                    <font-awesome-icon icon="chevron-up"/>
                                  </span>
                                </div>
                              </div>
                              <RichTextView v-if="activity.description && activity.description.length <= descriptionLength"
                                            :value="activity.description"
                                            :headings="false"
                                            compact
                                            class="text-formatted"
                              />
                              <p v-if="!activity.description" class="text-muted">
                                {{ $t('system.missing_input') }}
                              </p>
                            </div>
                            <br>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <ProjectSettingsPanels
                    :activity="activity"
                    :sections="['scope']"
                    :measureTask="measureTask"
                    @updated="updateActivity(false)"
                ></ProjectSettingsPanels>

                <ContactsTable :object="activity" @updated="updateActivity(false)"></ContactsTable>
              </div>

              <div class="v2-col">
                <EngagementScheduleCard :project="activity"></EngagementScheduleCard>
                <ProjectContributors :project="activity"
                                     :canManage="canManageContributors"
                                     @add="showAddContributorModal"
                                     @unlink="showUnlinkUserModal"
                ></ProjectContributors>
                <EngagementRecentActivity :project="activity" :key="'recent-' + activity.updated_at" @open-tab="openEngagementTab"></EngagementRecentActivity>
              </div>
            </div>

            <!-- Recurring projects: their settings -->
            <div class="row" v-if="isRecurringProject">
              <div class="col-lg-6">
                <ProjectSettingsPanels
                    :activity="activity"
                    :sections="['status']"
                    @complete="showConfirmCompleteModal"
                    @reopen="showConfirmReopenModal"
                    @close="showConfirmCloseModal"
                ></ProjectSettingsPanels>

                <ProjectSettingsPanels
                    :activity="activity"
                    :sections="['scope']"
                    :measureTask="measureTask"
                    @updated="updateActivity(false)"
                ></ProjectSettingsPanels>

                <ProjectContributors :project="activity"
                                     :canManage="canManageContributors"
                                     @add="showAddContributorModal"
                                     @unlink="showUnlinkUserModal"
                ></ProjectContributors>
              </div>

              <div class="col-lg-6">
                <RecurringProjectSettings
                    :project="activity"
                    :canEdit="isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && canManageProject && activity.status !== 'closed' && activity.status !== 'completed'"
                    @updated="recurringUpdated"
                ></RecurringProjectSettings>
              </div>
            </div>
          </div>
        </b-tab>


        <!-- Engagement acceptance tab -->
        <b-tab :title="$t('projects.engagement.tab.acceptance')"
               class="mt-3"
               ref="tab-acceptance"
               @click="refreshEngagement"
               v-if="isEngagementProject && canManageProject"
        >
          <div class="row" v-if="engagement.steps">
            <div class="col-lg-7">
              <EngagementPhase
                  :project="activity"
                  :engagement="engagement"
                  phase="acceptance"
                  @changed="engagementChanged"
                  @media-changed="loadContent"
                  @open-tab="openEngagementTab"
              >
                <!-- The audit scope is agreed before the engagement is accepted -->
                <template #before-acceptance>
                  <ProjectSettingsPanels
                      :activity="activity"
                      :sections="['scope']"
                      :measureTask="measureTask"
                      @updated="updateActivity(false)"
                  ></ProjectSettingsPanels>
                </template>
              </EngagementPhase>
            </div>
            <div class="col-lg-5">
              <EngagementTeam :project="activity" @updated="loadContent"></EngagementTeam>
            </div>
          </div>
        </b-tab>

        <!-- Planning tab -->
        <b-tab class="mt-3"
               ref="tab-planning"
               @click="refreshEngagement"
               v-if="isEngagementProject && canManageProject"
        >
          <template slot="title">
            {{ $t('projects.engagement.tab.planning') }}
            <font-awesome-icon icon="lock" class="text-muted ml-1" v-if="engagement.steps && !engagement.unlocked"/>
          </template>
          <div class="row" v-if="engagement.steps">
            <div class="col-lg-5">
              <EngagementPhase
                  :project="activity"
                  :engagement="engagement"
                  phase="planning"
                  @changed="engagementChanged"
                  @media-changed="loadContent"
                  @open-tab="openEngagementTab"
              ></EngagementPhase>

              <ProjectSettingsPanels
                  :activity="activity"
                  :sections="['schedule']"
                  class="mt-3"
                  @updated="updateActivity(false)"
              ></ProjectSettingsPanels>
            </div>
            <div class="col-lg-7">
              <EngagementBudget
                  :project="activity"
                  :engagement="engagement"
                  @changed="engagementChanged"
                  @reload="loadEngagement"
              ></EngagementBudget>

              <!-- CONTACTS (no gap above when the budget panel is switched off) -->
              <ContactsTable :object="activity" :class="{'mt-3': showsBudgetPanel}" @updated="updateActivity(false)"></ContactsTable>
            </div>
          </div>
        </b-tab>

        <!-- Process scope tab -->
        <b-tab :title="$t('projects.tab.process_scope')"
               ref="tab-scope"
               class="mt-3"
               @click="processTabClicked"
               v-if="((activity.scope === 'process' && activity.type !== 'security_measure' && activity.type !== 'control_assessment') && ((activity.additional_client && getClient.slug !== activity.additional_client.slug) || activity.additional_client == null))"
        >
          <div v-if="showProcessTab" class="pm-cols scope-tab">
            <ProjectScope :project="activity" kind="process" :canEdit="scopeEditable" @updated="scopeUpdated"></ProjectScope>
            <ScopeSettings :project="activity" :canEdit="scopeEditable" @updated="scopeUpdated"></ScopeSettings>
          </div>
        </b-tab>


        <!-- Activity scope tab -->
        <b-tab :title="$t('projects.tab.activity_scope')"
               ref="tab-scope"
               class="mt-3"
               @click="activityTabClicked"
               v-if="((activity.scope === 'activity' && activity.type !== 'security_measure' && activity.type !== 'control_assessment') && ((activity.additional_client && getClient.slug !== activity.additional_client.slug) || activity.additional_client == null))"
        >
          <div v-if="showActivityTab" class="pm-cols scope-tab">
            <ProjectScope :project="activity" kind="activity" :canEdit="scopeEditable" @updated="scopeUpdated"></ProjectScope>
            <ScopeSettings :project="activity" :canEdit="scopeEditable" @updated="scopeUpdated"></ScopeSettings>
          </div>
        </b-tab>

        <!-- System scope tab -->
        <b-tab :title="$t('projects.tab.system_scope')"
               ref="tab-scope"
               class="mt-3"
               @click="systemTabClicked"
               v-if="((activity.scope === 'system' && activity.type !== 'security_measure' && activity.type !== 'control_assessment' && activity.type !== 'bia_assessment') && ((activity.additional_client && getClient.slug !== activity.additional_client.slug) || activity.additional_client == null))"
        >
          <div v-if="showSystemTab" class="pm-cols scope-tab">
            <ProjectScope :project="activity" kind="system" :canEdit="scopeEditable" @updated="scopeUpdated"></ProjectScope>
            <ScopeSettings :project="activity" :canEdit="scopeEditable" @updated="scopeUpdated"></ScopeSettings>
          </div>
        </b-tab>


        <!-- Third party scope tab -->
        <b-tab :title="$t('projects.tab.third_party_scope')"
               ref="tab-scope"
               class="mt-3"
               @click="thirdPartyTabClicked"
               v-if="((activity.scope === 'third_party' && activity.type !== 'security_measure' && activity.type !== 'control_assessment' && activity.type !== 'bia_assessment') && ((activity.additional_client && getClient.slug !== activity.additional_client.slug) || activity.additional_client == null))"
        >
          <div v-if="showThirdPartyTab" class="pm-cols scope-tab">
            <ProjectScope :project="activity" kind="third_party" :canEdit="scopeEditable" @updated="scopeUpdated"></ProjectScope>
            <ScopeSettings :project="activity" :canEdit="scopeEditable" @updated="scopeUpdated"></ScopeSettings>
          </div>
        </b-tab>

        <!-- Policy scope tab -->
        <b-tab :title="$t('projects.tab.policy_scope')"
               ref="tab-scope"
               class="mt-3"
               @click="policyTabClicked"
               v-if="(activity.scope === 'policy' && activity.type !== 'security_measure' && activity.type !== 'control_assessment' && activity.type !== 'bia_assessment' && activity.type !== 'risk_assessment') && ((activity.additional_client && getClient.slug !== activity.additional_client.slug) || activity.additional_client == null)"
        >
          <div v-if="showPolicyTab" class="pm-cols scope-tab">
            <ProjectScope :project="activity" kind="policy" :canEdit="scopeEditable" @updated="scopeUpdated"></ProjectScope>
            <ScopeSettings :project="activity" :canEdit="scopeEditable" @updated="scopeUpdated"></ScopeSettings>
          </div>
        </b-tab>

        <!-- Control scope tab -->
        <b-tab :title="$t('projects.tab.control_scope')"
               ref="tab-scope"
               class="mt-3"
               @click="controlTabClicked"
               v-if="(activity.type === 'control' || activity.type === 'security_measure') && ((activity.additional_client && getClient.slug !== activity.additional_client.slug) || activity.additional_client === null)"
        >
          <div v-if="showControlTab" class="pm-cols scope-tab">
            <ProjectScope :project="activity" kind="control" :canEdit="scopeEditable" @updated="scopeUpdated"></ProjectScope>
            <ScopeSettings :project="activity" :kind="activity.type === 'control' ? 'control' : 'asset'" :canEdit="scopeEditable" @updated="scopeUpdated"></ScopeSettings>
          </div>
        </b-tab>

        <!-- Schedule tab -->
        <b-tab :title="$t('projects.tab.schedule')" class="mt-3" @click="scheduleTabClicked" v-if="activity.control_type === 'continuous'" ref="tab-schedule">
          <div v-if="showScheduleTab">
            <RecurringSchedule v-if="isRecurringProject"
                               :project="activity"
                               :canEdit="isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && canManageProject && activity.status !== 'closed' && activity.status !== 'completed'"
                               @updated="recurringUpdated"
            ></RecurringSchedule>
            <ScheduleTable v-else :object="activity" @updated="updateActivity(false)"></ScheduleTable>
            <ScopeSettings v-if="activity.type === 'risk_assessment'" class="mt-3" :project="activity" :canEdit="scopeEditable" @updated="scopeUpdated"></ScopeSettings>
          </div>
        </b-tab>

        <!-- Sub projects tab -->
        <b-tab :title="isRecurringProject ? $t('projects.recurring.rounds') : $t('projects.tab.sub_projects')"
               ref="tab-rounds"
               class="mt-3"
               @click="subProjectsTabClicked"
               v-if="activity.control_type === 'continuous'"
        >
          <div v-if="showSubProjectsTab">
            <RecurringRounds v-if="isRecurringProject"
                             :project="activity"
                             :key="'rounds-' + roundsKey"
                             :canRun="canManageProject && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity)"
                             @run="runRound"
                             @changed="roundsKey++"
            ></RecurringRounds>
            <ProjectsTable v-else :object="activity" class="mt-3"></ProjectsTable>
          </div>
        </b-tab>

        <!-- Relations  tab -->
        <!-- Relations belong to audits (US 9478) -->
        <b-tab :title="$t('maintenance.third_parties.tab.relations')" class="sub-menu" v-if="activity.type === 'control_assessment' && activity.include_relations">
          <b-tabs class="menu-smaller" v-model="subMenuIndex">
            <!-- Processes tab -->
            <b-tab :title="$t('systems.tab.processes')" class="mt-3">
              <ProjectScope :project="activity" kind="process" mode="relations" :canEdit="scopeEditable" @updated="scopeUpdated"></ProjectScope>
            </b-tab>
            <!-- Activities tab -->
            <b-tab :title="$t('systems.tab.activities')" class="mt-3">
              <ProjectScope :project="activity" kind="activity" mode="relations" :canEdit="scopeEditable" @updated="scopeUpdated"></ProjectScope>
            </b-tab>
            <!-- Systems tab -->
            <b-tab :title="$t('systems.tab.systems')" class="mt-3">
              <ProjectScope :project="activity" kind="system" mode="relations" :canEdit="scopeEditable" @updated="scopeUpdated"></ProjectScope>
            </b-tab>
            <!-- Third parties tab -->
            <b-tab :title="$t('systems.tab.third_party')" class="mt-3" v-if="getClient.contractBasicModule">
              <ProjectScope :project="activity" kind="third_party" mode="relations" :canEdit="scopeEditable" @updated="scopeUpdated"></ProjectScope>
            </b-tab>
          </b-tabs>
        </b-tab>

        <!-- System description tab: management statement and system description (US 9394) -->
        <b-tab :title="$t('projects.tab.statement_and_description')"
               class="mt-3"
               ref="tab-system_description"
               @click="measuresTabClicked"
               v-if="(activity.type === 'control_assessment' && activity.include_mapping && activity.control_type !== 'continuous') && ((activity.additional_client && getClient.slug !== activity.additional_client.slug) || activity.additional_client === null)"
        >
          <template slot='title'>
            {{$t('projects.tab.statement_and_description')}}
            <font-awesome-icon
                icon="exclamation-triangle"
                v-b-tooltip.hover
                :title="$t(missingStatementLabel)"
                class="risk-6"
                v-if="missingStatementLabel"
            />
          </template>

          <div v-if="showMeasuresTab">
            <div class="mt-3">
              <div class="row">

                <!-- LEFT (50%) -->
                <div class="col-lg-6">
                  <StatementTextPanel :project="activity"
                                      field="management_statement"
                                      title="projects.measures.management_statement_title"
                                      placeholder="projects.measures.management_statement_placeholder"
                                      missing-label="projects.measures.missing_management_statement"
                                      tag="management.statement"
                                      icon="file-signature"
                                      :can-edit="canManageContributors"
                                      qid="management-statement"
                                      :show-assignment="true"
                                      @updated="activity = $event"
                  ></StatementTextPanel>
                </div>

                <!-- RIGHT (50%) -->
                <div class="col-lg-6">
                  <StatementTextPanel :project="activity"
                                      field="system_description"
                                      title="projects.measures.system_description_title"
                                      placeholder="projects.measures.system_description_placeholder"
                                      missing-label="projects.measures.missing_system_description"
                                      tag="system.description"
                                      icon="desktop"
                                      :can-edit="canManageContributors"
                                      qid="system-description"
                                      :show-assignment="true"
                                      @updated="activity = $event"
                  ></StatementTextPanel>

                  <!-- System description attachments (Upload drawing), removed in US 9394. Kept here in case it's needed again.
                  <div class="panel">
                    <header class="panel__header">
                      <div class="profile-compact">
                        <div class="logo-container">
                          <font-awesome-icon icon="paperclip" class="icon-svg--sm"/>
                        </div>
                        <div class="profile-compact__info">
                          <div class="profile-compact__top">
                            {{ $t('projects.measures.system_description_drawing_title') }}
                          </div>
                        </div>
                      </div>
                    </header>
                    <div class="panel__body">
                <b-form-group>
                  <div v-show="showDropActive" class="drop-active">
                    <h3>{{ $t('tasks.modal.drop_files_to_upload_label') }}</h3>
                  </div>

                  <div class="form-control auto-height section-container pt-3 pb-3 mt-2">
                    <div class="text-center">
                      <p>
                        {{ $t('projects.measures.drag_and_drop_or') }}
                        <file-upload
                            ref="uploader-system-description"
                            v-model="files"
                            :drop="true"
                            input-id="template-file"
                            :multiple="false"
                            @input="onFileChangeEdit"
                            @dropActive="showDropActiveFunction"
                            class="text-success cursor-pointer"
                        >
                          {{ $t('system.click_here') }}
                        </file-upload>
                        {{ $t('projects.measures.to_upload_file') }}
                      </p>
                    </div>

                    <div v-if="activity.media && activity.media.length">
                      <p v-for="(file, index) in activity.media"
                         :key="index"
                      >
                        <span v-if="file.collection_name === 'system_description'">
                          <span class="float-right text-muted cursor-pointer"
                                @click="openDeleteAttachmentModal(file)"
                                data-qid="remove-media-item"
                          >
                            <font-awesome-icon icon="trash-alt" />
                          </span>

                          <span class="mr-3 float-right text-muted cursor-pointer"
                                @click="downloadMedia(file)"
                                data-qid="download-media"
                          >
                            <font-awesome-icon icon="cloud-download-alt" />
                          </span>

                          <a @click="downloadMedia(file)" data-qid="create-q-media-item">
                            {{ file.file_name }}
                          </a>
                        </span>
                      </p>

                    </div>
                  </div>
                </b-form-group>
                    </div>
                  </div>
                  -->
                </div>
              </div>

              <!-- System measures and Task contributors were removed from this tab in US 9394.
                   Kept here in case they are needed again.
              <div class="row">
                <div class="col-lg-5">
                  <MeasureScopeTable :object="activity" @updated="updateActivity(false)" :measureTask="measureTask"></MeasureScopeTable>
                </div>
                <div class="col-lg-7">
                  <MeasuresTable :object="activity" v-if="activity.control_type !== 'continuous'"></MeasuresTable>
                </div>
              </div>
              -->
            </div>
          </div>
        </b-tab>

        <!-- Tasks tab -->
        <b-tab :title="$t('projects.tab.tasks')"
               class="mt-3"
               ref="tab-tasks"
               v-if="activity.control_type === 'one time'"
               @click="tasksTabClicked">
          <template slot='title'>
            {{$t('projects.tab.tasks')}}
            <font-awesome-icon icon="lock" class="text-muted ml-1" v-if="isEngagementLocked"/>
            <font-awesome-icon
                    icon="exclamation-triangle"
                    v-b-tooltip.hover
                    :title="$t('tasks.messages.unresolved_tasks')"
                    class="risk-6"
                    v-if="activity.progress !== 100"
            />
          </template>

          <div v-if="showTasksTab">
            <EngagementLockedNotice v-if="isEngagementLocked"></EngagementLockedNotice>
            <TasksTable v-if="(activity.type !== 'control_assessment' && activity.type !== 'risk_assessment')" :object="activity" @updated="updateActivity(false)"></TasksTable>

            <AssessmentTasksTable
                v-if="!isEngagementLocked && (activity.type === 'control_assessment' || activity.type === 'risk_assessment') && ((activity.additional_client && getClient.slug !== activity.additional_client.slug) || activity.additional_client === null)"
                :object="activity"
                @updated="updateActivity(false)"
            ></AssessmentTasksTable>
            <AssessmentTasksTableReadOnly
                v-if="(activity.type === 'control_assessment' || activity.type === 'risk_assessment') && (isEngagementLocked || (activity.additional_client && getClient.slug === activity.additional_client.slug))"
                :object="activity"
                @updated="updateActivity(false)"
            ></AssessmentTasksTableReadOnly>
          </div>
        </b-tab>

        <!-- Documents tab -->
        <b-tab :title="$t('projects.engagement.tab.documents')"
               class="mt-3"
               ref="tab-documents"
               @click="refreshEngagement"
               v-if="isEngagementProject"
        >
          <EngagementDocuments
              v-if="engagement.steps"
              :project="activity"
              :engagement="engagement"
              @changed="engagementChanged"
              @media-changed="loadContent"
          ></EngagementDocuments>
        </b-tab>

        <!-- Reporting tab -->
        <b-tab class="mt-3"
               ref="tab-reporting"
               v-if="activity.control_type === 'one time' && (activity.type === 'control_assessment' || activity.type === 'risk_assessment' || activity.type === 'bia_assessment')"
        >
          <template slot="title">
            {{ $t('projects.tab.reporting') }}
            <font-awesome-icon icon="lock" class="text-muted ml-1" v-if="isEngagementLocked"/>
          </template>
          <EngagementLockedNotice v-if="isEngagementLocked"></EngagementLockedNotice>
          <Reporting v-else :object="activity" :measureTask="measureTask"></Reporting>
        </b-tab>

        <!-- Settings tab -->
        <b-tab :title="$t('projects.engagement.tab.settings')"
               class="mt-3"
               ref="tab-settings"
               title-item-class="d-none"
               v-if="(isEngagementProject && canManageProject) || isOneTimeProject"
        >
          <!-- Other one-time projects: status, schedule and the project's settings (US 9478) -->
          <div class="row" v-if="isOneTimeProject" data-qid="project-settings">
            <div class="col-lg-5">
              <ProjectSettingsPanels
                  :activity="activity"
                  :sections="['status']"
                  @complete="showConfirmCompleteModal"
                  @reopen="showConfirmReopenModal"
                  @close="showConfirmCloseModal"
              ></ProjectSettingsPanels>

              <ProjectSettingsPanels
                  :activity="activity"
                  :sections="['schedule']"
                  @updated="updateActivity(false)"
              ></ProjectSettingsPanels>
            </div>
            <div class="col-lg-7">
              <ProjectSettingsCard
                  :project="activity"
                  :canEdit="isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && canManageProject && activity.status !== 'closed' && activity.status !== 'completed'"
                  @updated="loadContent"
              ></ProjectSettingsCard>
            </div>
          </div>

          <div class="row" v-else>
            <div class="col-lg-5">
              <ProjectSettingsPanels
                  :activity="activity"
                  :sections="['status']"
                  @complete="showConfirmCompleteModal"
                  @reopen="showConfirmReopenModal"
                  @close="showConfirmCloseModal"
              ></ProjectSettingsPanels>

              <EngagementTeam :project="activity" @updated="loadContent"></EngagementTeam>

              <EngagementProjectSettings :project="activity" @updated="loadContent" @open-tab="openEngagementTab"></EngagementProjectSettings>
            </div>
            <div class="col-lg-7">
              <EngagementSettings
                  v-if="engagement.steps"
                  :project="activity"
                  :engagement="engagement"
                  @changed="engagementChanged"
              ></EngagementSettings>
            </div>
          </div>
        </b-tab>

        <!-- Project history tab -->
        <b-tab :title="$t('projects.tab.history')"
               class="mt-3"
               ref="tab-history"
               title-item-class="d-none"
               @click="historyTabClicked"
               v-if="(activity.additional_client && getClient.slug !== activity.additional_client.slug) || activity.additional_client === null"
        >
          <div v-if="showHistoryTab">
            <ProjectHistory :object="activity"></ProjectHistory>
          </div>
        </b-tab>

      </b-tabs>


      <!-- Add contributor modal -->
      <b-modal ref="add-contributor-modal" modal-class="task-modal" @hide="hideAddContributorModal">
        <template #modal-header>
          <div class="" style="width: 100%;">
            <span @click="hideAddContributorModal" class="btn btn-secondary cursor-pointer float-right">
              <font-awesome-icon icon="times" />
            </span>
            <h5 class="mb-1 text-center" data-qid="add-contributor-title">
              {{ $t('projects.view.add_contributor') }}
            </h5>
          </div>
        </template>

        <div class="form-container">
          <b-alert v-if="error.length" show variant="danger" data-qid="add-contributor-error">
            {{ error }}
          </b-alert>

          <b-form-group>
            <label data-qid="add-contributor-users-label">
              {{ $t('projects.view.contributors_label') }}
            </label>

            <div class="multiselect-row">
              <multiselect v-model="selectedContributors"
                           :options="contributorOptions"
                           :multiple="true"
                           :taggable="false"
                           :internal-search="false"
                           :disabled="buttonDisabled"
                           track-by="slug"
                           label="name"
                           :select-label="$t('system.dropdown_select_label')"
                           :selected-label="$t('system.dropdown_selected_label')"
                           :deselect-label="$t('system.dropdown_deselect_label')"
                           :placeholder="$t('projects.view.contributors_label')"
                           @search-change="loadContributorUsers"
                           data-qid="add-contributor-users-select"
              >
                <template slot="tag" slot-scope="props">
                  <span class="mr-3 user-tag">
                    <Avatar :object="props.option" size="xs"></Avatar>
                    <span class="ml-2">{{ props.option.name }}</span>
                  </span>
                </template>
                <template slot="option" slot-scope="props">
                  <UserDropdownOption :user="props.option"></UserDropdownOption>
                </template>
              </multiselect>

              <UserInviteModal :buttonType="'small'" :buttonDisabled="buttonDisabled" @reloadUserList="loadContributorUsers()">
              </UserInviteModal>
            </div>
          </b-form-group>
        </div>

        <template #modal-footer>
          <div class="" style="width: 100%;">
            <div class="alert alert-menu-color color-secondary mb-0" role="alert">
              <div class="text-right">
                <b-button variant="secondary" @click="hideAddContributorModal" data-qid="add-contributor-cancel-button">
                  {{ $t('projects.create.cancel') }}
                </b-button>
                <b-button class="ml-3"
                          variant="success"
                          :disabled="buttonDisabled || !selectedContributors.length"
                          @click="addContributors"
                          data-qid="add-contributor-submit-button"
                >
                  <span v-if="buttonDisabled" class="mr-1">
                    <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                  </span>
                  {{ $t('projects.create.submit') }}
                </b-button>
              </div>
            </div>
          </div>
        </template>
      </b-modal>

      <!-- Unlink contributor modal -->
      <b-modal ref="unlink-user-modal" modal-class="task-modal" @hide="hideUnlinkUserModal">
        <template #modal-header>
          <div class="" style="width: 100%;">
            <span @click="hideUnlinkUserModal" class="btn btn-secondary cursor-pointer float-right">
              <font-awesome-icon icon="times" />
            </span>
            <h5 class="mb-1 text-center" data-qid="unlink-contributor-title">
              {{ $t('projects.view.unlink_contributor') }}
            </h5>
          </div>
        </template>

        <div class="form-container">
          <b-alert v-if="error.length" show variant="danger" data-qid="unlink-contributor-error">
            {{ error }}
          </b-alert>
          <p class="text-center mb-0">
            {{ $t('projects.view.unlink_contributor_message', { name: selectedUnlinkUser.name, project: activity.title }) }}
          </p>
        </div>

        <template #modal-footer>
          <div class="" style="width: 100%;">
            <div class="alert alert-menu-color color-secondary mb-0" role="alert">
              <div class="text-right">
                <b-button variant="secondary" @click="hideUnlinkUserModal" data-qid="unlink-contributor-cancel-button">
                  {{ $t('projects.create.cancel') }}
                </b-button>
                <b-button class="ml-3"
                          variant="danger"
                          :disabled="buttonDisabled"
                          @click="unlinkUser"
                          data-qid="unlink-contributor-submit-button"
                >
                  <span v-if="buttonDisabled" class="mr-1">
                    <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                  </span>
                  {{ $t('projects.view.unlink_contributor_submit') }}
                </b-button>
              </div>
            </div>
          </div>
        </template>
      </b-modal>

      <!-- Re-assign modal -->
      <b-modal ref="re-assign" hide-footer @hide="hideEditUsersModal">
        <div class="d-block text-center">
          <h4 data-qid="update-tp-title">
            {{ $t('tasks.re_assign.title') }}
          </h4>
        </div>
        <b-alert v-if="error.length"
                 show
                 variant="danger"
                 data-qid="create-p-error"
        >
          {{ error }}
        </b-alert>
        <b-form @submit="reassignFormSubmit" qid="create-p-form" v-if="selectedTask && selectedTask.model">
          <div class="form-container">
            <b-form-group>
              <label data-qid="create-p-users-label">
                {{ $t('projects.create.owners_label') }}
              </label>
              <multiselect
                  v-model="selectedAssignUsers"
                  :options="allUsers"
                  :multiple="true"
                  :taggable="false"
                  track-by="slug"
                  :select-label="$t('system.dropdown_select_label')"
                  :selected-label="$t('system.dropdown_selected_label')"
                  :deselect-label="$t('system.dropdown_deselect_label')"
                  :placeholder="$t('projects.create.owners_placeholder')"
                  label="name"
                  data-qid="create-p-users-select"
              >
                <template slot="tag" slot-scope="props">
                  <span class="mr-3 user-tag">
                    <Avatar :object="props.option" size="xs"></Avatar>
                    <span class="ml-2">{{ props.option.name }}</span>
                  </span>
                </template>
                <template slot="option" slot-scope="props">
                  <UserDropdownOption :user="props.option"></UserDropdownOption>
                </template>
              </multiselect>
            </b-form-group>
          </div>
          <div class="text-right">
            <b-button class="mt-3"
                      variant="secondary"
                      @click="hideEditUsersModal"
                      data-qid="create-p-cancel-button"
            >
              {{ $t('projects.create.cancel') }}
            </b-button>
            <b-button type="submit"
                      class="mt-3 ml-3"
                      variant="success"
                      :disabled="buttonDisabled"
                      data-qid="create-p-submit-button"
            >
              <span v-if="buttonDisabled" class="mr-1">
                <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
              </span>
              {{ $t('projects.create.submit') }}
            </b-button>
          </div>
        </b-form>
      </b-modal>


      <b-modal ref="view-task-modal" hide-footer @hide="hideViewTaskModal">
        <div class="d-block text-center">
          <h4 data-qid="update-tp-title">
            {{ $t('tasks.modal.view_title') }}
          </h4>
        </div>
        <b-form qid="create-p-form" v-if="selectedTask && selectedTask.model">
          <div class="form-container">
            <b-form-group class="mb-4">
              <div v-if="selectedTask.scope">
                <font-awesome-icon icon="shield-alt" size="lg" class="mr-2"/>
                {{ selectedTask.scope.name }}
              </div>
              <div v-if="selectedTask.model && selectedTask.project.type !== 'security_measure'" class="mt-3">
                <span v-if="selectedTask.model">
                  <font-awesome-icon icon="desktop"
                                     size="lg"
                                     class="mr-2"
                                     v-if="selectedTask.model.who_am_i === 'App\\System\\Models\\System'"
                  />
                  <font-awesome-icon icon="cubes"
                                     size="lg"
                                     class="mr-2"
                                     v-if="selectedTask.model.who_am_i === 'App\\Activity\\Models\\Activity'"
                  />
                  <font-awesome-icon icon="cube"
                                     size="lg"
                                     class="mr-2"
                                     v-if="selectedTask.model.who_am_i === 'App\\Process\\Models\\Process'"
                  />
                  <font-awesome-icon icon="file-signature"
                                     size="lg"
                                     class="mr-2"
                                     v-if="selectedTask.model.who_am_i === 'App\\Maintenance\\ThirdParty\\Models\\ThirdParty'"
                  />
                  <font-awesome-icon icon="file-invoice"
                                     size="lg"
                                     class="mr-2"
                                     v-if="selectedTask.model.who_am_i === 'App\\Maintenance\\Policy\\Models\\Policy'"
                  />
                </span>
                {{ selectedTask.model.name }}
              </div>
            </b-form-group>
            <b-form-group>
              <label data-qid="create-p-users-label">
                {{ $t('tasks.modal.project_message_label') }}
              </label>
              <div v-if="selectedTask.project.message && selectedTask.project.message.length > 300">
                <p v-if="showFullProjectMessage" class="text-formatted">
                  {{ selectedTask.project.message }}
                </p>
                <p v-if="!showFullProjectMessage" class="text-formatted">
                  {{ selectedTask.project.message | truncate(300) }}
                </p>
                <div class="text-center mt-3" @click="showFullProjectMessage=!showFullProjectMessage">
                  <span class="cursor-pointer" v-if="!showFullProjectMessage">
                    <span class="mr-2">
                      {{ $t('system.view_more') }}
                    </span>
                    <font-awesome-icon icon="chevron-down"/>
                  </span>
                  <span class="cursor-pointer" v-if="showFullProjectMessage">
                    <span class="mr-2">
                      {{ $t('system.view_less') }}
                    </span>
                    <font-awesome-icon icon="chevron-up"/>
                  </span>
                </div>
              </div>
              <p v-if="selectedTask.project.message && selectedTask.project.message.length <= 300"
                 class="text-formatted"
              >
                {{ selectedTask.project.message }}
              </p>
            </b-form-group>
            <b-form-group v-if="selectedTask && selectedTask.scope">
              <label data-qid="create-p-users-label">
                {{ $t('tasks.modal.generic_control_description_label') }}
              </label>
              <div v-if="selectedTask.scope.description && selectedTask.scope.description.length > 300">
                <p v-if="showFullGenericControlDescription" class="text-formatted">
                  {{ selectedTask.scope.description }}
                </p>
                <p v-if="!showFullGenericControlDescription" class="text-formatted">
                  {{ selectedTask.scope.description | truncate(300) }}
                </p>
                <div class="text-center mt-3" @click="showFullGenericControlDescription=!showFullGenericControlDescription">
                  <span class="cursor-pointer" v-if="!showFullGenericControlDescription">
                    <span class="mr-2">
                      {{ $t('system.view_more') }}
                    </span>
                    <font-awesome-icon icon="chevron-down"/>
                  </span>
                  <span class="cursor-pointer" v-if="showFullGenericControlDescription">
                    <span class="mr-2">
                      {{ $t('system.view_less') }}
                    </span>
                    <font-awesome-icon icon="chevron-up"/>
                  </span>
                </div>
              </div>
              <p v-if="selectedTask.scope.description && selectedTask.scope.description.length <= 300"
                 class="text-formatted"
              >
                {{ selectedTask.scope.description }}
              </p>
            </b-form-group>
            <b-form-group>
              <label data-qid="create-p-users-label">
                {{ $t('tasks.modal.status_label') }}
              </label>
              <p>
                <ControlResultBadge :value="selectedTask.completion_status"></ControlResultBadge>
              </p>
            </b-form-group>
            <b-form-group>
              <label data-qid="create-p-users-label">
                {{ $t('tasks.modal.remarks_label') }}
              </label>
              <FormattedNoteText :value="selectedTask.remarks"></FormattedNoteText>
            </b-form-group>
            <div class="mt-2">
              <label>
                {{ $t('maintenance.policies.create.attachments') }}
              </label>
              <br>
              <p v-for="(file, index) in selectedTask.media" :key="index">
                <span class="mr-3 float-right text-muted cursor-pointer"
                      @click="downloadMedia(file)"
                      data-qid="download-media"
                >
                  <font-awesome-icon icon="cloud-download-alt" />
                </span>
                <a href="#" @click="downloadMedia(file)" data-qid="create-p-media-item">
                  {{ file.file_name }}
                </a>
              </p>
            </div>
            <div class="mt-4">
              <label>
                {{ $t('maintenance.policies.create.links_label') }}
              </label>
              <div v-if="selectedTask.links">
                <p v-for="(link, index) in selectedTask.links" :key="index">
                  <a :href="link.url" target="_blank" data-qid="create-p-link-item">
                    {{ link.name ? link.name : link.url }}
                  </a>
                </p>
              </div>
              <br>
            </div>
          </div>
          <div class="text-right">
            <b-button @click="hideViewTaskModal"
                      class="ml-3"
                      variant="success"
                      qid="create-p-submit-button"
            >
              {{ $t('system.close') }}
            </b-button>
          </div>
        </b-form>
      </b-modal>


      <!-- Delete project -->
      <b-modal ref="delete-activity" modal-class="v2-modal" hide-footer>
        <template #modal-header>
          <div class="us-modal-head">
            <h5 class="mb-0" qid="delete-p-title">{{ $t('activities.delete.title') }}</h5>
            <button type="button" class="us-icon-btn" :aria-label="$t('activities.delete.cancel')" @click="hideConfirmDeleteModal">
              <font-awesome-icon icon="times"/>
            </button>
          </div>
        </template>
        <div class="us-remove">
          <span class="us-remove__icon"><font-awesome-icon icon="trash-alt"/></span>
          <b>{{ itemToDelete.title }}</b>
        </div>
        <p class="mt-3 mb-0">{{ $t('activities.delete.are_you_sure') }}</p>
        <div class="us-modal-foot">
          <span class="us-grow"></span>
          <b-button variant="secondary" qid="delete-s-cancel-button" @click="hideConfirmDeleteModal">
            {{ $t('activities.delete.cancel') }}
          </b-button>
          <b-button variant="danger"
                    :disabled="deleting"
                    qid="delete-s-submit-button"
                    v-if="isGranted([permissions.PROJECT_DELETE, permissions.PROJECT_DELETE_OWN], activity)"
                    @click="confirmDelete(itemToDelete)"
          >
            <font-awesome-icon v-if="deleting" icon="spinner" class="fa-spin mr-1"/>{{ $t('activities.delete.submit') }}
          </b-button>
        </div>
      </b-modal>


      <!-- Complete Project modal template -->
      <b-modal ref="complete-project" hide-footer modal-class="task-modal">
        <template #modal-header="{ close }">
          <div class="" style="width: 100%;">
            <span @click="hideConfirmCompleteModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
              <font-awesome-icon icon="times"/>
            </span>
            <h5 class="mb-1 text-center" data-qid="create-p-title">
              {{ $t('projects.confirm.complete') }}
            </h5>
          </div>
        </template>

        <h5 class="text-center">
          {{ $t('projects.confirm.complete_description') }}<br />{{ itemToChangeStatus.title }}
        </h5>

        <div class="proxy-footer">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="text-center">
              <b-button class=""
                        variant="secondary"
                        @click="hideConfirmCompleteModal"
                        qid="delete-s-cancel-button"
              >
                {{ $t('activities.delete.cancel') }}
              </b-button>
              <b-button @click="completeProject()"
                        class="ml-3"
                        variant="success"
                        qid="delete-s-submit-button"
                        v-if="isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], itemToChangeStatus)"
              >
                {{ $t('projects.project.update.submit') }}
              </b-button>
            </div>
          </div>
        </div>
      </b-modal>


      <!-- Close Project modal template -->
      <b-modal ref="close-project" hide-footer modal-class="task-modal">
        <template #modal-header="{ close }">
          <div class="" style="width: 100%;">
            <span @click="hideConfirmCloseModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
              <font-awesome-icon icon="times"/>
            </span>
            <h5 class="mb-1 text-center" data-qid="create-p-title">
              {{ $t('projects.confirm.close') }}
            </h5>
          </div>
        </template>

        <h5 class="text-center">
          {{ $t('projects.confirm.close_description') }}<br />{{ itemToChangeStatus.title }}
        </h5>

        <div class="proxy-footer">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="text-center">
              <b-button class=""
                        variant="secondary"
                        @click="hideConfirmCloseModal"
                        qid="delete-s-cancel-button"
              >
                {{ $t('activities.delete.cancel') }}
              </b-button>
              <b-button @click="closeProject()"
                        class="ml-3"
                        variant="success"
                        qid="delete-s-submit-button"
                        v-if="isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], itemToChangeStatus)"
              >
                {{ $t('projects.project.update.submit') }}
              </b-button>
            </div>
          </div>
        </div>
      </b-modal>


      <!-- Reopen Project modal template -->
      <b-modal ref="reopen-project" hide-footer modal-class="task-modal">
        <template #modal-header="{ close }">
          <div class="" style="width: 100%;">
            <span @click="hideConfirmReopenModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
              <font-awesome-icon icon="times"/>
            </span>
            <h5 class="mb-1 text-center" data-qid="create-p-title">
              {{ $t('projects.confirm.reopen') }}
            </h5>
          </div>
        </template>

        <h5 class="text-center">
          {{ $t('projects.confirm.reopen_description') }}<br />{{ itemToChangeStatus.title }}
        </h5>

        <div class="proxy-footer">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="text-center">
              <b-button class=""
                        variant="secondary"
                        @click="hideConfirmReopenModal"
                        qid="delete-s-cancel-button"
              >
                {{ $t('activities.delete.cancel') }}
              </b-button>
              <b-button @click="reopenProject()"
                        class="ml-3"
                        variant="success"
                        qid="delete-s-submit-button"
                        v-if="isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], itemToChangeStatus)"
              >
                {{ $t('projects.project.update.submit') }}
              </b-button>
            </div>
          </div>
        </div>
      </b-modal>

      <ConfirmRemoveAttachment
          :object="selectedFile"
          :show="showDeleteAttachmentModal"
          type="file"
          @closeConfirmModal="closeDeleteAttachmentModal"
      ></ConfirmRemoveAttachment>

      <ConfirmRemoveAttachment
          :object="selectedLink"
          :show="showDeleteLinkModal"
          type="link"
          @closeConfirmModal="closeDeleteLinkModal"
      ></ConfirmRemoveAttachment>

      <ShowMoreModal
          :items="selectedItem.tags_translated"
          :show="showTagsModal"
          @closeViewModal="closeTagsModal"
          type="tags"
      ></ShowMoreModal>
    </div>


    <!-- Independence declaration modal -->
    <b-modal ref="independence-declaration-modal"
             modal-class="task-modal"
             no-close-on-backdrop
             no-close-on-esc
    >
      <template #modal-header>
        <div class="" style="width: 100%;">
          <h5 class="mb-1 text-center" data-qid="independence-declaration-title">
            {{ $t('projects.view.independence.title') }}
          </h5>
        </div>
      </template>

      <div class="form-container">
        <b-alert v-if="error.length" show variant="danger" data-qid="independence-declaration-error">
          {{ error }}
        </b-alert>
        <p class="text-formatted mb-0" v-if="!confirmIndependenceRejection">{{ $t('projects.view.independence.message', { project: independenceDeclaration.project_title, client: independenceDeclaration.client_name }) }}</p>
        <p class="text-formatted mb-0" v-else>{{ $t('projects.view.independence.reject_confirm_message', { client: independenceDeclaration.client_name }) }}</p>
      </div>

      <template #modal-footer>
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="text-right" v-if="!confirmIndependenceRejection">
              <b-button variant="secondary"
                        :disabled="buttonDisabled"
                        @click="confirmIndependenceRejection = true"
                        data-qid="independence-declaration-reject-button"
              >
                {{ $t('projects.view.independence.reject_button') }}
              </b-button>
              <b-button class="ml-3"
                        variant="success"
                        :disabled="buttonDisabled"
                        @click="submitIndependenceDeclaration(true)"
                        data-qid="independence-declaration-approve-button"
              >
                <span v-if="buttonDisabled" class="mr-1">
                  <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                </span>
                {{ $t('projects.view.independence.approve_button', { client: independenceDeclaration.client_name }) }}
              </b-button>
            </div>
            <div class="text-right" v-else>
              <b-button variant="secondary"
                        :disabled="buttonDisabled"
                        @click="confirmIndependenceRejection = false"
                        data-qid="independence-declaration-back-button"
              >
                {{ $t('projects.view.independence.back_button') }}
              </b-button>
              <b-button class="ml-3"
                        variant="danger"
                        :disabled="buttonDisabled"
                        @click="submitIndependenceDeclaration(false)"
                        data-qid="independence-declaration-confirm-reject-button"
              >
                <span v-if="buttonDisabled" class="mr-1">
                  <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                </span>
                {{ $t('projects.view.independence.reject_confirm_button') }}
              </b-button>
            </div>
          </div>
        </div>
      </template>
    </b-modal>

    <div v-if="show404">
      <page404></page404>
    </div>

    <div v-if="show403">
      <page403></page403>
    </div>

    <!-- Runs a new round of a recurring project; the launcher's own button is not shown -->
    <span style="display:none;" v-if="isRecurringProject">
      <CreateProjectModal ref="launcher" @reloadContent="roundStarted"/>
    </span>

  </div>
</template>

<script>
import {initialsAvatarUrl} from '@/util/userAvatar.js';
import piincHttp from '@/util/configureAxios.js';
import {config} from '@/config.js';
import ProjectEditForm from '@/views/Projects/ProjectEditForm.vue';
import Avatar from '@/components/Avatar.vue';
import UserDropdownOption from '@/components/UserDropdownOption.vue';
import ProjectHistory from '@/views/Projects/ProjectHistory.vue';
import ControlAssessmentScopeTable from '@/views/Projects/ControlAssessmentScopeTable.vue';
// Removed from the System description tab in US 9394, kept in case they are needed again
// import MeasureScopeTable from '@/views/Projects/MeasureScopeTable.vue';
import ScheduleTable from '@/views/Projects/ScheduleTable.vue';
import ProjectsTable from '@/views/Projects/ProjectsTable.vue';
import ConfirmRemoveAttachment from '@/components/ConfirmRemoveAttachment.vue';
import TasksTable from '@/views/Projects/TasksTable.vue';
import AssessmentTasksTable from '@/views/Projects/AssessmentTasksTable.vue';
import AssessmentTasksTableReadOnly from '@/views/Projects/AssessmentTasksTableReadOnly.vue';
import ObservationsTable from '@/views/Projects/ObservationsTable.vue';
// import MeasuresTable from '@/views/Projects/MeasuresTable.vue';
import StatementTextPanel from '@/views/Projects/StatementTextPanel.vue';
import Reporting from '@/views/Projects/Reporting.vue';
import RichTextEditor from '@/components/richText/RichTextEditor.vue';
import RichTextView from '@/components/richText/RichTextView.vue';
import ShowMoreModal from '@/components/ShowMoreModal.vue';
import page404 from '@/components/404block.vue';
import page403 from '@/components/403block.vue';
import ContactsTable from '@/views/Projects/ContactsTable.vue';
import UserInviteModal from '@/components/user/UserInviteModal.vue';
import ProjectSettingsPanels from '@/views/Projects/ProjectSettingsPanels.vue';
import EngagementOverview from '@/views/Projects/Engagement/EngagementOverview.vue';
import EngagementTeam from '@/views/Projects/Engagement/EngagementTeam.vue';
import EngagementHero from '@/views/Projects/Engagement/EngagementHero.vue';
import EngagementScheduleCard from '@/views/Projects/Engagement/EngagementScheduleCard.vue';
import EngagementRecentActivity from '@/views/Projects/Engagement/EngagementRecentActivity.vue';
import EngagementProjectSettings from '@/views/Projects/Engagement/EngagementProjectSettings.vue';
import EngagementPhase from '@/views/Projects/Engagement/EngagementPhase.vue';
import EngagementBudget from '@/views/Projects/Engagement/EngagementBudget.vue';
import { restoreRail } from '@/util/sidebarRail.js';
import EngagementDocuments from '@/views/Projects/Engagement/EngagementDocuments.vue';
import EngagementSettings from '@/views/Projects/Engagement/EngagementSettings.vue';
import EngagementLockedNotice from '@/views/Projects/Engagement/EngagementLockedNotice.vue';
import ProjectLockedNotice from '@/components/ProjectLockedNotice.vue';
import { isEngagement, budgetEnabled } from '@/views/Projects/Engagement/engagement.js';
import CreateProjectModal from '@/views/Projects/CreateProjectModal.vue';
import RecurringProjectHero from '@/views/Projects/Recurring/RecurringProjectHero.vue';
import ProjectHero from '@/views/Projects/ProjectHero.vue';
import ProjectContributors from '@/views/Projects/ProjectContributors.vue';
import ProjectSettingsCard from '@/views/Projects/ProjectSettingsCard.vue';
import FormattedNoteText from '@/components/tasks/FormattedNoteText.vue';
import RecurringProjectOverview from '@/views/Projects/Recurring/RecurringProjectOverview.vue';
import RecurringSchedule from '@/views/Projects/Recurring/RecurringSchedule.vue';
import RecurringRounds from '@/views/Projects/Recurring/RecurringRounds.vue';
import RecurringProjectSettings from '@/views/Projects/Recurring/RecurringProjectSettings.vue';
import ProjectScope from '@/views/Projects/Scope/ProjectScope.vue';
import ScopeSettings from '@/views/Projects/Scope/ScopeSettings.vue';

export default {
  name: 'ProjectView',
  data: function() {
    return {
      activity: {},
      engagement: {},
      edit: this.$route.params.edit,
      selectedSort: '',
      selectedDirection: '',
      lbTableItems: [],
      lbLoading: true,
      showInput: false,

      itemToDelete: {},
      deleting: false,
      itemToChangeStatus: {},
      users: [],
      error: '',
      lbForm: {
        name: '',
        article_reason: '',
        links: []
      },
      inputLink: '',
      selectedFiles: [],
      selectedFile: {},
      selectedLink: {},
      showDeleteAttachmentModal: false,
      showDeleteLinkModal: false,
      showDescriptionInput: false,
      activityEditDescription: '',
      descriptionLength: config.descriptionLength,
      showFullDescription: false,
      showFullGenericControlDescription: false,
      showFullProjectMessage: false,
      // Tabs
      showActivityTab: false,
      showProcessTab: false,
      showControlTab: false,
      showSystemTab: false,
      showAssessmentTab: false,
      showThirdPartyTab: false,
      showPolicyTab: false,
      showScheduleTab: false,
      showSubProjectsTab: false,
      showMeasuresTab: false,
      showTasksTab: false,
      showSystemRiskTab: false,
      showObservationTab: false,
      showHistoryTab: false,

      accept: 'image/jpg, image/jpeg, image/png',
      extensions: 'jpg, jpeg, png',
      files: [],

      buttonDisabled: false,
      tabIndex: 0,
      roundsKey: 0,
      // Settings or History, opened from the engagement hero
      activeEngagementTab: '',
      myGuides: [],
      open_tasks: [],
      closed_tasks: [],
      showStatusForm: false,
      options: [
        { name: this.$t('system.pending'), value: 'pending' },
        { name: this.$t('system.in_progress'), value: 'in progress' },
        { name: this.$t('system.completed'), value: 'completed' },
        { name: this.$t('system.closed'), value: 'closed' }
      ],
      selectedStatus: [],
      selectedTask: [],
      selectedAssignUsers: [],
      itemObject: {},
      allUsers: [],
      measureTask: {},
      selectedItem: {},
      showTagsModal: false,
      show404: false,
      show403: false,
      independenceDeclaration: {},
      confirmIndependenceRejection: false,
      selectedUnlinkUser: {},
      selectedContributors: [],
      contributorUsers: [],
      subMenuIndex: 0,
      showDropActive: false,
      selectedLanguage: {},
      languages: [
        {value: 'en', text: 'English'},
        {value: 'da', text: 'Danish'},
        {value: 'fr', text: 'French'},
        {value: 'de', text: 'German'},
        {value: 'sv', text: 'Swedish'},
        {value: 'nl', text: 'Dutch'},
        {value: 'es', text: 'Spanish'}
      ]
    }
  },
  computed: {
    getClient() {
      return this.$store.state.client
    },
    // The System description tab warns while the management statement or the system description is missing
    missingStatementLabel() {
      if (!this.activity.management_statement && !this.activity.system_description) {
        return 'projects.measures.missing_statement_and_description';
      }
      if (!this.activity.management_statement) {
        return 'projects.measures.missing_management_statement';
      }
      if (!this.activity.system_description) {
        return 'projects.measures.missing_system_description';
      }
      return null;
    },
    getFiles() {
      return this.selectedFiles
    },
    getUser() {
      return this.$store.state.user
    },
    // Who can change the scope; an engagement's relations are managed by its Managers
    scopeEditable() {
      return this.isGranted([this.permissions.PROJECT_EDIT, this.permissions.PROJECT_EDIT_OWN], this.activity) && this.canManageProject &&
        this.activity.status !== 'closed' && this.activity.status !== 'completed' &&
        ((this.activity.additional_client && this.getClient.slug !== this.activity.additional_client.slug) || this.activity.additional_client == null);
    },
    isRecurringProject() {
      return this.activity.control_type === 'continuous' && !this.isEngagementProject;
    },
    isEngagementProject() {
      return isEngagement(this.activity);
    },
    // One-time projects of the other types: the project hero with Settings and History in it (US 9478)
    isOneTimeProject() {
      return this.activity.control_type === 'one time' && !this.isEngagementProject;
    },
    // Same conditions as the Budget and time panel itself
    showsBudgetPanel() {
      return !!(this.activity.engagement_permissions && this.activity.engagement_permissions.view_budget) && budgetEnabled(this.activity);
    },
    canManageProject() {
      // On engagements only Managers change the project itself; Associates work on tasks and reporting
      return !this.isEngagementProject || !!(this.activity.engagement_permissions && this.activity.engagement_permissions.manage);
    },
    isEngagementLocked() {
      return this.isEngagementProject && !!this.engagement.steps && !this.engagement.unlocked;
    },
    canManageContributors() {
      return this.isGranted([this.permissions.PROJECT_EDIT, this.permissions.PROJECT_EDIT_OWN], this.activity) && this.activity.status !== 'closed' && this.activity.status !== 'completed' && ((this.activity.additional_client && this.getClient.slug !== this.activity.additional_client.slug) || this.activity.additional_client == null);
    },
    contributorOptions() {
      // Users that are already contributors on the project can't be added again
      const existingIds = _.map(this.activity.users || [], 'id');
      return _.filter(this.contributorUsers, user => !existingIds.includes(user.id));
    }
  },
  watch: {
    // Settings and History aren't in the engagement tab bar; the hero buttons show when they are open
    tabIndex() {
      this.$nextTick(() => {
        const settings = this.isRecurringProject ? this.$refs['tab-overview'] : this.$refs['tab-settings'];
        const history = this.$refs['tab-history'];
        this.activeEngagementTab = settings && settings.localActive ? 'settings' : (history && history.localActive ? 'history' : '');
      });
    },
    // Watch when client changes in store and trigger api reload
    getClient: function (newValue, oldValue) {
      if (newValue && oldValue && newValue.slug && oldValue.slug && newValue.slug !== oldValue.slug) {
        this.goToList(this.activity)
      } else {
        this.loadContent();
      }
    },
  },
  components: {
    CreateProjectModal,
    RecurringProjectHero,
    ProjectHero,
    ProjectContributors,
    ProjectSettingsCard,
    FormattedNoteText,
    RecurringProjectOverview,
    RecurringSchedule,
    RecurringRounds,
    RecurringProjectSettings,
    ProjectScope,
    ScopeSettings,
    RichTextEditor,
    RichTextView,
    UserDropdownOption,
    ProjectEditForm,
    Avatar,
    ProjectHistory,
    ControlAssessmentScopeTable,
    // MeasureScopeTable,
    ScheduleTable,
    ProjectsTable,
    ConfirmRemoveAttachment,
    TasksTable,
    AssessmentTasksTable,
    AssessmentTasksTableReadOnly,
    ObservationsTable,
    // MeasuresTable,
    StatementTextPanel,
    Reporting,
    ShowMoreModal,
    page403,
    page404,
    ContactsTable,
    UserInviteModal,
    ProjectSettingsPanels,
    EngagementOverview,
    EngagementTeam,
    EngagementHero,
    EngagementScheduleCard,
    EngagementRecentActivity,
    EngagementProjectSettings,
    EngagementPhase,
    EngagementBudget,
    EngagementDocuments,
    EngagementSettings,
    EngagementLockedNotice,
    ProjectLockedNotice
  },
  methods: {
    openTagsModal(object) {
      this.selectedItem = object;
      this.showTagsModal = true;
    },
    closeTagsModal() {
      this.selectedItem = {};
      this.showTagsModal = false;
    },
    getReducedTags(tags) {
      let reducedTags = [];
      if (tags.length > 3) {
        reducedTags = _.cloneDeep(tags);
        reducedTags = reducedTags.slice(0, 3);
      } else {
        reducedTags = tags;
      }
      return reducedTags;
    },
    goToList(object) {
      if (object.type === 'control_assessment' || object.type === 'risk_assessment' || object.type === 'bia_assessment') {
        this.$router.push('/projects?view=list&family=assessments');
      } else if (object.type === 'update' || object.type === 'validation') {
        this.$router.push('/projects?view=list&family=records');
      } else if (object.type === 'control' || object.type === 'security_measure') {
        this.$router.push('/projects?view=list&family=controls');
      }
    },
    resetFile() {
      let imageFile = document.querySelector('#file');
      imageFile.value = '';
    },
    loadContent() {
      let self = this;
      piincHttp.get('projects/' + this.$route.params.slug, { params:
        {
          client: this.getClient.slug
        }
      }).then(function(response) {
        self.activity = response.data;

        if (isEngagement(self.activity)) {
          self.loadEngagement();
        }

        if (self.activity.language) {
          self.selectedLanguage = self.languages.find(lang => lang.value === self.activity.language);
        }

        if (self.activity.include_mapping) {
          // Call API for open tasks
          piincHttp.get('projects/' + self.$route.params.slug + '/tasks', { params:
            {
              client: self.getClient.slug,
              code: 'update_measures',
              per_page: 1,
            }
          }).then(function(response) {
            self.measureTask = response.data.items[0];

            if (response.data.items.length) {
              self.activity.task_count_measure = 1;
            } else {
              self.activity.task_count_measure = 0;
            }
          }, function() {});
        }
      }, function(error) {
        if (error.status === 404) {
          self.show404 = true;
        }
        if (error.status === 403 && error.data && error.data.independence_declaration) {
          self.handleIndependenceDeclaration(error.data);
        } else if (error.status === 403) {
          self.show403 = true;
        }
      });


    },
    loadEngagement() {
      let self = this;
      piincHttp.get('projects/' + this.$route.params.slug + '/engagement').then(function(response) {
        self.engagement = response.data;
      }, function() {});
    },
    refreshEngagement() {
      // Task changes (e.g. moving tasks to OE testing) change the engagement progress
      if (this.isEngagementProject) {
        this.loadEngagement();
      }
    },
    engagementChanged(data) {
      const wasUnlocked = this.engagement.unlocked;
      this.engagement = data;

      // Accepting or withdrawing the engagement changes what the user may do on the project
      if (wasUnlocked !== data.unlocked) {
        this.loadContent();
      }
    },
    // Opens a tab of a recurring project from its hero or overview, with the tab's own loading
    openRecurringTab(name) {
      const refs = {base: 'tab-overview', scope: 'tab-scope', schedule: 'tab-schedule', rounds: 'tab-rounds', history: 'tab-history'};
      let tab = this.$refs[refs[name]] || this.$refs['tab-overview'];
      tab = Array.isArray(tab) ? tab[0] : tab;
      if (tab && tab.activate) {
        tab.activate();
        tab.$emit('click');
      }
    },
    // The scope components save themselves; the page reloads the project when they report a change
    scopeUpdated(project) {
      if (project && project.slug) {
        this.recurringUpdated(project);
      } else {
        this.loadContent();
      }
    },
    // Saving can give the project a new slug; the page follows it
    recurringUpdated(project) {
      if (project && project.slug && project.slug !== this.$route.params.slug) {
        this.$router.replace({ name: 'projects.view', params: { slug: project.slug }});
      } else {
        this.loadContent();
      }
    },
    runRound() {
      this.$refs.launcher.showRunModal(this.activity);
    },
    roundStarted() {
      this.roundsKey++;
      this.loadContent();
    },
    openEngagementTab(name) {
      const tab = this.$refs['tab-' + name];
      if (tab && tab.activate) {
        tab.activate();
        if (name === 'tasks') {
          this.tasksTabClicked();
        }
        if (name === 'system_description') {
          this.measuresTabClicked();
        }
        if (name === 'history') {
          this.historyTabClicked();
        }
      }
    },
    handleIndependenceDeclaration(data) {
      this.independenceDeclaration = {
        status: data.independence_declaration,
        client_name: data.client_name,
        project_title: data.project_title
      };

      this.error = '';
      this.confirmIndependenceRejection = false;
      this.$nextTick(() => {
        this.$refs['independence-declaration-modal'].show();
      });
    },
    submitIndependenceDeclaration(independent) {
      let self = this;
      this.error = '';
      this.buttonDisabled = true;

      piincHttp.post('projects/' + this.$route.params.slug + '/independence-declaration', {
        independent: independent
      }).then(function() {
        self.buttonDisabled = false;
        self.$refs['independence-declaration-modal'].hide();

        if (independent) {
          self.independenceDeclaration = {};
          self.loadContent();
        } else {
          self.$toastr('error', self.$t('projects.view.independence.rejected_message', { client: self.independenceDeclaration.client_name }));
          self.$router.push('/');
        }
      }, function(error) {
        self.buttonDisabled = false;
        self.error = error.data && error.data.message ? error.data.message : '';
      });
    },
    showAddContributorModal() {
      this.error = '';
      this.selectedContributors = [];
      this.loadContributorUsers();
      this.$refs['add-contributor-modal'].show();
    },
    hideAddContributorModal() {
      this.error = '';
      this.selectedContributors = [];
      this.$refs['add-contributor-modal'].hide();
    },
    loadContributorUsers(query) {
      let self = this;
      piincHttp.get('users', { params:
        {
          client: this.getClient.id,
          keyword: query ? query : undefined,
          per_page: 100
        }
      }).then(function(response) {
        self.contributorUsers = response.data.items;
      }, function() {});
    },
    addContributors() {
      let self = this;
      this.buttonDisabled = true;

      piincHttp.put('projects/' + this.$route.params.slug, {
        users: _.uniq(_.map(this.activity.users || [], 'id').concat(_.map(this.selectedContributors, 'id')))
      }).then(function() {
        self.buttonDisabled = false;
        self.hideAddContributorModal();
        self.$toastr('success', self.$t('projects.view.add_contributor_success'));
        self.loadContent();
      }, function(error) {
        self.buttonDisabled = false;
        self.error = error.data && error.data.message ? error.data.message : '';
      });
    },
    showUnlinkUserModal(user) {
      this.error = '';
      this.selectedUnlinkUser = user;
      this.$refs['unlink-user-modal'].show();
    },
    hideUnlinkUserModal() {
      this.error = '';
      this.selectedUnlinkUser = {};
      this.$refs['unlink-user-modal'].hide();
    },
    unlinkUser() {
      let self = this;
      const userId = this.selectedUnlinkUser.id;
      this.buttonDisabled = true;

      piincHttp.put('projects/' + this.$route.params.slug, {
        users: _.map(_.filter(this.activity.users, function(user) { return user.id !== userId; }), 'id')
      }).then(function() {
        self.buttonDisabled = false;
        self.hideUnlinkUserModal();
        self.$toastr('success', self.$t('projects.view.unlink_contributor_success'));
        self.loadContent();
      }, function(error) {
        self.buttonDisabled = false;
        self.error = error.data && error.data.message ? error.data.message : '';
      });
    },
    showViewTaskModal(object) {
      this.error = '';

      this.selectedTask = object;
      this.$refs['view-task-modal'].show();
    },
    hideViewTaskModal() {
      this.error = '';

      this.selectedTask = {};
      this.$refs['view-task-modal'].hide();
    },
    updateActivity() {
      this.loadContent();
    },
    historyTabClicked() {
      this.showHistoryTab = true;
      this.showSystemTab = false;
      this.showThirdPartyTab = false;
      this.showPolicyTab = false;
      this.showProcessTab = false;
      this.showActivityTab = false;
      this.showSubProjectsTab = false;
      this.showTasksTab = false;
      this.showSystemRiskTab = false;
      this.showObservationTab = false;
      this.showAssessmentTab = false;
    },
    basicTabClicked() {
      this.showHistoryTab = false;
      this.showSystemTab = false;
      this.showThirdPartyTab = false;
      this.showPolicyTab = false;
      this.showProcessTab = false;
      this.showActivityTab = false;
      this.showTasksTab = false;
      this.showSystemRiskTab = false;
      this.showObservationTab = false;
      this.showScheduleTab = false;
      this.showSubProjectsTab = false;
      this.showMeasuresTab = false;
      this.showAssessmentTab = false;
    },
    subProjectsTabClicked() {
      this.showHistoryTab = false;
      this.showSystemTab = false;
      this.showThirdPartyTab = false;
      this.showPolicyTab = false;
      this.showProcessTab = false;
      this.showActivityTab = false;
      this.showTasksTab = false;
      this.showSystemRiskTab = false;
      this.showObservationTab = false;
      this.showScheduleTab = false;
      this.showSubProjectsTab = true;
      this.showMeasuresTab = false;
      this.showAssessmentTab = false;
    },
    activityTabClicked() {
      this.showActivityTab = true;
      this.showHistoryTab = false;
      this.showTasksTab = false;
      this.showSystemRiskTab = false;
      this.showObservationTab = false;
      this.showScheduleTab = false;
      this.showSubProjectsTab = false;
      this.showMeasuresTab = false;
      this.showAssessmentTab = false;
    },
    systemTabClicked() {
      this.showSystemTab = true;
      this.showHistoryTab = false;
      this.showTasksTab = false;
      this.showSystemRiskTab = false;
      this.showObservationTab = false;
      this.showScheduleTab = false;
      this.showSubProjectsTab = false;
      this.showMeasuresTab = false;
      this.showAssessmentTab = false;
    },
    assessmentTabClicked() {
      this.showAssessmentTab = true;
      this.showHistoryTab = false;
      this.showSystemTab = false;
      this.showThirdPartyTab = false;
      this.showProcessTab = false;
      this.showActivityTab = false;
      this.showTasksTab = false;
      this.showSystemRiskTab = false;
      this.showObservationTab = false;
      this.showScheduleTab = false;
      this.showSubProjectsTab = false;
      this.showMeasuresTab = false;
    },
    thirdPartyTabClicked() {
      this.showThirdPartyTab = true;
      this.showHistoryTab = false;
      this.showTasksTab = false;
      this.showSystemRiskTab = false;
      this.showObservationTab = false;
      this.showScheduleTab = false;
      this.showSubProjectsTab = false;
      this.showMeasuresTab = false;
      this.showAssessmentTab = false;
    },
    policyTabClicked() {
      this.showPolicyTab = true;
      this.showThirdPartyTab = false;
      this.showHistoryTab = false;
      this.showTasksTab = false;
      this.showSystemRiskTab = false;
      this.showObservationTab = false;
      this.showScheduleTab = false;
      this.showSubProjectsTab = false;
      this.showMeasuresTab = false;
      this.showAssessmentTab = false;
    },
    processTabClicked() {
      this.showHistoryTab = false;
      this.showProcessTab = true;
      this.showTasksTab = false;
      this.showSystemRiskTab = false;
      this.showObservationTab = false;
      this.showScheduleTab = false;
      this.showSubProjectsTab = false;
      this.showMeasuresTab = false;
      this.showAssessmentTab = false;
    },
    controlTabClicked() {
      this.showControlTab = true;
      this.showHistoryTab = false;
      this.showTasksTab = false;
      this.showSystemRiskTab = false;
      this.showObservationTab = false;
      this.showScheduleTab = false;
      this.showSubProjectsTab = false;
      this.showMeasuresTab = false;
      this.showAssessmentTab = false;
    },
    tasksTabClicked() {
      this.showTasksTab = true;
      this.showSystemRiskTab = false;
      this.showObservationTab = false;
      this.showScheduleTab = false;
      this.showSubProjectsTab = false;
      this.showMeasuresTab = false;
      this.showAssessmentTab = false;
    },
    measuresTabClicked() {
      this.showMeasuresTab = true;
      this.showScheduleTab = false;
      this.showSubProjectsTab = false;
      this.showHistoryTab = false;
      this.showSystemTab = false;
      this.showThirdPartyTab = false;
      this.showProcessTab = false;
      this.showActivityTab = false;
      this.showTasksTab = false;
      this.showSystemRiskTab = false;
      this.showObservationTab = false;
      this.showAssessmentTab = false;
    },
    scheduleTabClicked() {
      this.showScheduleTab = true;
      this.showSubProjectsTab = false;
      this.showMeasuresTab = false;
      this.showHistoryTab = false;
      this.showSystemTab = false;
      this.showThirdPartyTab = false;
      this.showProcessTab = false;
      this.showActivityTab = false;
      this.showTasksTab = false;
      this.showSystemRiskTab = false;
      this.showObservationTab = false;
      this.showAssessmentTab = false;
    },
    handleCloseViewPModal () {
      this.loadPolicies();
      this.viewPolicyModal = false;
      this.selectedPolicy = [];
    },
    showDescriptionInputFunction() {
      this.showDescriptionInput = true;
      this.activityEditDescription = this.activity.description;
    },
    formDescriptionSubmit() {
      let self = this;
      piincHttp.put('projects/' + this.$route.params.slug, {
        description: this.activityEditDescription
      }).then(function(response) {
        self.activity = response.data;
        self.showDescriptionInput = false;
        self.$toastr('success', self.$t('projects.update.success'))
      }, function(error) {
        if (error.status !== 200) {
          self.error = error.data.message;
        }
      })
    },
    completeProject() {
      let self = this;
      piincHttp.put('projects/' + this.$route.params.slug, {
        status: "completed"
      }).then(function(response) {
        self.activity = response.data;
        self.$refs['complete-project'].hide();
        self.$toastr('success', self.$t('projects.update.success'));
      }, function(error) {
        if (error.status !== 200) {
          self.error = error.data.message;
        }
      })
    },
    closeProject() {
      let self = this;
      piincHttp.put('projects/' + this.$route.params.slug, {
        status: "closed"
      }).then(function(response) {
        self.activity = response.data;
        self.$refs['close-project'].hide();
        self.$toastr('success', self.$t('projects.update.success'));
      }, function(error) {
        if (error.status !== 200) {
          self.error = error.data.message;
        }
      })
    },
    reopenProject() {
      let self = this;
      piincHttp.put('projects/' + this.$route.params.slug, {
        status: "in progress"
      }).then(function(response) {
        self.activity = response.data;
        self.$refs['reopen-project'].hide();
        self.$toastr('success', self.$t('projects.update.success'));
      }, function(error) {
        if (error.status !== 200) {
          self.error = error.data.message;
        }
      })
    },
    cancelDescriptionForm() {
      this.showDescriptionInput = false;
      this.activityEditDescription = this.activity.description;
    },
    openViewPolicyModal(object) {
      this.selectedPolicy = object;
      this.viewPolicyModal = true;
    },
    reloadContent(object) {
      if (this.tabIndex === 1) {
        this.tabIndex = 0;
      }
      // If slug is not changed, just reload content
      if (object.slug === this.activity.slug) {
        this.loadContent();
      } else {
        // If slug changed, reload state with new param
        this.$router.push({ name: 'projects.view', params: { slug: object.slug }});
      }
    },
    getUserAvatar(user){
      if (user && user.avatar) {
        return user.avatar;
      } else {
        return initialsAvatarUrl(user);
      }
    },
    showConfirmDeleteModal(object) {
      this.itemToDelete = object;
      this.$refs['delete-activity'].show();
    },
    showConfirmCompleteModal(object) {
      this.itemToChangeStatus = object;
      this.$refs['complete-project'].show();
    },
    showConfirmCloseModal(object) {
      this.itemToChangeStatus = object;
      this.$refs['close-project'].show();
    },
    showConfirmReopenModal(object) {
      this.itemToChangeStatus = object;
      this.$refs['reopen-project'].show();
    },
    hideConfirmReopenModal() {
      this.itemToChangeStatus = {};
      this.$refs['reopen-project'].hide();
    },
    hideConfirmDeleteModal() {
      this.itemToDelete = {};
      this.$refs['delete-activity'].hide();
    },
    hideConfirmCompleteModal() {
      this.itemToChangeStatus = {};
      this.$refs['complete-project'].hide();
    },
    hideConfirmCloseModal() {
      this.itemToChangeStatus = {};
      this.$refs['close-project'].hide();
    },
    confirmDelete(object) {
      let self = this;
      self.deleting = true;
      piincHttp.delete('projects/' + object.slug).then(function() {
        self.deleting = false;
        self.hideConfirmDeleteModal();
        self.goToList(object);
        // Trigger toastr message after modal close
        self.$toastr('success', self.$t('projects.delete.success'));
      }, function() {
        self.deleting = false;
      });
    },
    handleLink() {
      let self = this;
      let formData = new FormData();

      if (!this.inputLink.startsWith('http')) {
        this.inputLink = 'https://' + this.inputLink;
      }

      formData.append("url", this.inputLink);
      formData.append("model_type", this.activity.who_am_i);
      formData.append("model_id", this.activity.id);

      piincHttp.post('links', formData).then(function() {
        self.$toastr('success', self.$t('maintenance.third_parties.link_add_success'));
        self.loadContent();
        self.inputLink = '';
        self.showInput = false;
      }, function() {})
    },
    downloadMedia(object){
      piincHttp.get('documents/' + object.id + '/download', {responseType: 'blob'}).then(function(response) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = object.file_name;

        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }, function() {});
    },
    openDeleteAttachmentModal(object) {
      this.selectedFile = object;
      this.showDeleteAttachmentModal = true;
    },
    closeDeleteAttachmentModal () {
      this.selectedFile = {};
      this.showDeleteAttachmentModal = false;
      this.loadContent();
    },
    openDeleteLinkModal(object) {
      this.selectedLink = object;
      this.showDeleteLinkModal = true;
    },
    closeDeleteLinkModal () {
      this.selectedLink = {};
      this.showDeleteLinkModal = false;
      this.loadContent();
    },
    onFileChange(file) {
      let files = file.target.files;
      let self = this;
      let formData = new FormData();
      let sizeError = false;

      for (let i = 0; i < files.length; i++) {
        // Equal to 10 MB
        if (files[i].size > 10485760) {
          sizeError = true;
        }
        formData.append('document[]', files[i]);
      }

      formData.append("name", "documents");
      formData.append("model_type", this.activity.who_am_i);
      formData.append("model_id", this.activity.id);

      if (sizeError) {
        this.error = this.$t('system.file_size');
      } else {
        piincHttp.post('documents', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(function() {
          self.$toastr('success', self.$t('maintenance.third_parties.media_add_success'));
          self.error = '';
          self.loadContent();
        }, function(error) {
          self.error = error.data.message;
          if (error.data.errors['document.0']) {
            self.error += ' ' + error.data.errors['document.0'][0];
          }
        })
      }
    },
    showDropActiveFunction(value) {
      this.showDropActive = value;
    },
    onFileChangeEdit() {
      let self = this;
      let formData = new FormData();
      let sizeError = false;

      // Append list of attachments to FormData
      for (let i = 0; i < this.files.length; i++) {
        // Equal to 10 MB
        if (this.files[i].size > 31457280) {
          sizeError = true;
        }
        formData.append("document[]", this.files[i].file);
      }

      formData.append("name", "documents");
      formData.append("collection", "system_description");
      formData.append("model_type", this.activity.who_am_i);
      formData.append("model_id", this.activity.id);

      if (sizeError) {
        this.error = this.$t('system.file_size_30');
        this.files = [];
      } else {
        piincHttp.post('documents', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(function() {
          self.$toastr('success', self.$t('maintenance.third_parties.media_add_success'));
          self.error = '';
          self.loadContent();
          // reset the model array
          self.files = []
        }, function(error) {
          self.error = error.data.message;
          if (error.data.errors['document.0']) {
            self.error += ' ' + error.data.errors['document.0'][0];
          }
        })
      }
    },
    loadGuides() {
      let self = this;

      piincHttp.get('guides', { params:
        {
          per_page: 100,
          language: 'en',
          client: this.getClient.slug
        }
      }).then(function(response) {
        if (response.data.items.length) {
          self.myGuides = response.data.items;
        }
      }, function() {});
    },
    sendReminders(object) {
      let self = this;
      // Call API for reminders
      piincHttp.get('projects/' + object.slug + '/reminders').then(function() {
        self.$toastr('success', self.$t('projects.create.project_reminders_success'));
      }, function() {});
    },
    getReducedUsers(users) {
      let reducedUsers = [];
      if (users && users.length > 3) {
        reducedUsers = _.cloneDeep(users);
        reducedUsers = reducedUsers.slice(0, 3);
      } else {
        reducedUsers = users;
      }
      return reducedUsers;
    },
    formStatusSubmit() {
      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;

      piincHttp.put('projects/' + this.activity.slug, {
        status: this.selectedStatus.value
      }).then(function(response) {
        self.activity = response.data;
        self.cancelForm();
        self.loadContent();
        self.$toastr('success', self.$t('projects.update.success'));
        self.buttonDisabled = false;
      }, function(error) {
        if (error.status !== 200) {
          self.buttonDisabled = false;
          self.error = error.data.message;
        }
      })
    },
    hideEditUsersModal() {
      this.selectedTask = {};
      this.$refs['re-assign'].hide();
    },
    showEditUsersModal(object) {
      let self = this;
      this.error = '';

      let role_name = [self.permissions.ROLE_CLIENT_ADMIN];

      if (self.activity.type === 'control' || self.activity.type === 'security_measure') {
        role_name.push(self.permissions.ROLE_MEMBER);
        role_name.push(self.permissions.ROLE_VALIDATOR);
      }

      if (this.getClient && this.getClient.parent) {
        role_name.push(self.permissions.ROLE_PARTITION_ADMIN);
      }

      this.selectedAssignUsers = [];
      piincHttp.get('users', { params:
        {
          client: this.getClient.id,
          role_name: role_name,
          per_page: 100
        }
      }).then(function(response) {
        let users = response.data.items;
        _.forEach(object.users, function (user) {
          if (_.findIndex(users, function(o) { return o.id === user.id; }) === -1) {
            if (user.id !== self.getUser.id) {
              users.push(user);
            }
          }
          if (user.id !== self.getUser.id) {
            let matchedUser = _.cloneDeep(user);
            if (user.id === object.owner) {
              matchedUser.owner = true;
            }
            self.selectedAssignUsers.push(matchedUser);
          }
        })
        self.allUsers = _.uniq(users);
      }, function() {});

      this.selectedTask = object;
      this.$refs['re-assign'].show();
    },
    reassignFormSubmit(evt) {
      this.buttonDisabled = true;
      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;
      let userIds = [];
      evt.preventDefault();

      if (this.selectedAssignUsers && this.selectedAssignUsers.length) {
        for (let i = 0; i < this.selectedAssignUsers.length; i++) {
          userIds.push(this.selectedAssignUsers[i].id);
        }
      }

      if (userIds && userIds.length) {
        piincHttp.put('tasks/' + this.selectedTask.id, {
          users: userIds
        }).then(function() {
          self.hideEditUsersModal();
          self.loadOpenTasks();
          self.$toastr('success', self.$t('tasks.re_assign.success'));
          self.buttonDisabled = false;
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      } else {
        self.error = self.$t('tasks.re_assign.required')
        self.buttonDisabled = false;
      }
    },
    showFormFunction() {
      let self = this;
      this.selectedStatus = [];
      if (this.activity.status) {
        this.selectedStatus = _.find(this.options, function(item) {
          return item.value === self.activity.status;
        });
      }
      this.showStatusForm = true;
    },
    loadOpenTasks() {
      let self = this;
      // Call API for open tasks
      piincHttp.get('projects/' + this.activity.slug + '/tasks', { params:
        {
          status: 'Pending',
          per_page: 100,
          client: this.getClient.slug
        }
      }).then(function(response) {
        self.open_tasks = response.data.items;
      }, function() {});
    },
  },
  created () {
    if (this.getClient && this.getClient.slug) {
      this.loadContent();
    }
  },
  beforeDestroy () {
    // The project page uses the icon rail for more room; give the user's own choice back (US 9372)
    restoreRail();
  },
  mounted () {
    let topElement = document.getElementById("app");
    if (topElement) {
      if (window.innerWidth > 768) {
        topElement.classList.add("sidebar-collapsed");
      } else {
        topElement.classList.remove("sidebar-collapsed");
      }
    }
  }
}
</script>
